import { type ReactElement, useEffect } from 'react'
import { type DataTestIdAttribute } from '../../types'
import { type TypographyProps } from '../../primitives/Typography'
import { Box } from '../../primitives/Box'
import { Stack } from '../../primitives/Stack'
import { Divider } from '../../primitives/Divider'
import { Ellipsis } from '../../primitives/Ellipsis'
import { useCard } from './use-card'

type InternalProps = {
  title: string
  summary?: string | null
  endNode?: ReactElement | null
  isOpen?: boolean
  pb?: number

  /**
   * What element do you want to give our title
   *
   * @default 'p'
   */
  component?: TypographyProps['component']
} & DataTestIdAttribute

export type CardHeaderProps = Omit<InternalProps, 'summary'>

// internal component
const CardHeaderInternal = ({ summary, title, isOpen, endNode, pb, component = 'p', ...rest }: InternalProps) => {
  const { isDense, spacingToken, isMobile, hasDividerTop, setHasHeader } = useCard()

  useEffect(() => {
    setHasHeader(true)

    return () => setHasHeader(false)
  }, [setHasHeader])

  return (
    <Box sx={{ px: isDense ? 2 : spacingToken, pt: 2, pb: hasDividerTop ? 2 : pb, width: '100%' }} {...rest}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minHeight: ({ spacing }) => spacing(3.75) }}>
        <Box sx={{ flex: 1, textAlign: 'left' }}>
          <Ellipsis variant="h5" component={component}>
            {title}
          </Ellipsis>
          {!isOpen && summary && <Ellipsis variant={isMobile ? 'body2' : 'body1'}>{summary}</Ellipsis>}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            ml: 'auto',
            alignSelf: 'center',
            gap: 1,
          }}
        >
          {endNode}
        </Box>
      </Stack>
    </Box>
  )
}

export const BaseCardHeader = (props: CardHeaderProps) => {
  const { hasDividerTop, isMobile } = useCard()

  // reason: create helper when more usecases
  // eslint-disable-next-line no-nested-ternary
  const pb = isMobile ? 0 : hasDividerTop ? 2 : 0

  return (
    <>
      <CardHeaderInternal {...props} pb={pb} />
      {hasDividerTop && <Divider />}
    </>
  )
}

export const BaseCardHeaderAccordion = (props: InternalProps) => {
  const { hasDividerTop, isMobile } = useCard()

  // reason: create helper when more usecases, not destcructuring since passing all below
  // eslint-disable-next-line no-nested-ternary, react/destructuring-assignment
  const pb = hasDividerTop ? 2 : props.isOpen ? 0 : 2

  return (
    <>
      <CardHeaderInternal {...props} pb={pb} />
      {!isMobile && <Divider />}
    </>
  )
}
